<template>
	<MainLayout>
		<LoaderPage v-if="loadingPage" />
		<HeaderUnidade />
		<main :class="theme === 'dark' ? 'tw-bg-[#151515]' : 'tw-bg-white'" class="tw-min-h-screen">
			<div class="tw-mx-auto tw-px-6 tw-py-6">
				<div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-10 tw-gap-6">
					<div class="lg:tw-col-span-8 tw-space-y-6">
					<div class="tw-relative">
						<DashboardTable 
							:silos="silos" 
							:unidade="unidade"
						/>
					</div>
					<div class="tw-relative">
						<LoaderComponent v-if="loadingGrafico" />
						<DashboardGraficoEstacao 
							ref="dashboardGraficoEstacao" 
							:unidade="unidade"
							@update-loading="updateLoadingGrafico"
						/>
					</div>
					</div>
					<div class="lg:tw-col-span-2 tw-space-y-6">
						<div class="tw-relative">
							<DashboardArmazenagemTotal 
								:percentage="armazenadoPorcentagem"
								:armazenadoTotal="armazenadoTotal"
								:capacidadeTotal="capacidadeTotal"
							/>
						</div>
						<div class="tw-relative">
							<LoaderComponent v-if="loadingEstacao" />
							<DashboardEstacao 
                        :estacao="estacao"
							/>
						</div>
					</div>
				</div>
			</div>
		</main>
	</MainLayout>
</template>

<script>
import HeaderUnidade from "@/components/ui/v2/HeaderUnidade.vue";
import MainLayout from "@/components/layouts/v2/MainLayout.vue";
import LoaderPage from '@/components/ui/v2/LoaderPage.vue';
import LoaderComponent from '@/components/ui/v2/LoaderComponent.vue';
import DashboardTable from '@/components/ui/v2/Dashboard/DashboardTable.vue';
import DashboardGraficoEstacao from '@/components/ui/v2/Dashboard/DashboardGraficoEstacao.vue';
import DashboardArmazenagemTotal from '@/components/ui/v2/Dashboard/DashboardArmazenagemTotal.vue';
import DashboardEstacao from '@/components/ui/v2/Dashboard/DashboardEstacao.vue';
import "@/assets/css/pages/unidade-page.css";

import { ref, onMounted, computed, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';

export default {
	components: {
        MainLayout,
		LoaderPage,
		LoaderComponent,
		HeaderUnidade,
		DashboardTable,
		DashboardGraficoEstacao,
		DashboardArmazenagemTotal,
		DashboardEstacao
	},
	methods: {
		updateLoading(value) {
			this.loadingPage = value;
		},
		updateLoadingGrafico(value) {
			this.loadingGrafico = value;
		},
	},
	setup() {
		const store = useStore();
		const route = useRoute();
		const unidade = ref({});
		const silos = ref([]);
		const toast = useToast();
		const dashboardGraficoEstacao = ref(null);
        const estacao = ref({});

		const loadingPage = ref(true);
		const selectedDate = ref(new Date().toISOString().slice(0, 10));
		const inputDate = ref(selectedDate.value);
		const loadingGrafico = ref(false);
		const loadingEstacao = ref(false);

		const formatarNumero = (numero) => {
			let numeroInt = parseInt(numero);
			return new Intl.NumberFormat('pt-BR').format(numeroInt);
		};

        const getEstacaoByUnidade = async () => {
			loadingEstacao.value = true;
            estacao.value = await store.dispatch("unidade/getEstacaoByUnidade", route.params.unidade);
			loadingEstacao.value = false;
        }

		const getDashboardData = async () => {
			loadingPage.value = true;

			unidade.value = {};
			silos.value = [];

			const unidadeId = route.params.unidade;
            getEstacaoByUnidade();

			try {
				await store.dispatch("unidade/fetchUnidade", unidadeId);
				unidade.value = store.state.unidade.unidade;

				await store.dispatch("silos/fetchSilosByUnidade", unidadeId);
				let idsilos = store.state.silos.silos;
				
				for (const item of idsilos) {
					await store.dispatch("silo/fetchSiloById", item.id);
					silos.value.push(store.state.silo.silo);
				}

				const carregarSilos = silos.value.map(async (silo) => {
					try {
						const leituraMaisRecente = silo.leituras[0];

						const leitura = await store.dispatch("leituras/fetchLeituraById", {
							siloId: silo.id,
							leituraId: leituraMaisRecente._id
						});
						silo.ultimaleitura = leitura;

						if (silo.existe_aeracao == 1) {
							let totalSegundos = silo.aeracao.horas_aeradas.horas_day;
							let horas = Math.floor(totalSegundos / 3600);
							let minutos = Math.floor((totalSegundos % 3600) / 60);
							let segundos = totalSegundos % 60;
							silo.aeracaoultimas24horas = `${horas} Hrs ${minutos} min ${segundos} s`
						}

					
					} catch (error) {
						console.error(`Erro ao buscar leituras para ${silo.nome}:`, error);
						toast.error(`Erro ao buscar leituras para ${silo.nome}`);
					}
				});
            await Promise.all(carregarSilos);

			} catch (error) {
				console.error("Erro ao buscar unidade ou silos:", error);
				toast.error("Erro ao buscar dados");
			}

			if (dashboardGraficoEstacao.value) {
				dashboardGraficoEstacao.value.fetchEstacaoData();
			}

			
			loadingPage.value = false;
		}

		onMounted(async () => {
			getDashboardData();
		});

		watch(() => route.params.unidade, async () => {
			getDashboardData();
		});

		const capacidadeTotal = computed(() => {
			return formatarNumero(silos.value.reduce((total, silo) => {
				return total + (parseInt(silo.capacidade));
			}, 0));
		});

		const armazenadoTotal = computed(() => {
			return formatarNumero(silos.value.reduce((total, silo) => {
				let nivelAtual = silo.tipo_nivel == 1 ? silo.nivel_automatico : silo.nivel_manual;
				return total + ((nivelAtual / 100) * parseInt(silo.capacidade));
			}, 0));
		});

		const formattedDate = computed(() => {
			const dateParts = selectedDate.value.split('-');
			const date = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
			return date.toLocaleDateString('pt-BR');
		});

		const armazenadoPorcentagem = computed(() => {
			const total = (capacidadeTotal.value).replace(/\./g, '').replace(',', '.');
			const armazenado = (armazenadoTotal.value).replace(/\./g, '').replace(',', '.');
			return (parseFloat(armazenado) / parseFloat(total)) * 100;
		});

		const theme = computed(() => store.state.ui.theme);

		return {
			unidade,
			silos,
			capacidadeTotal,
			armazenadoTotal,
			selectedDate,
			inputDate,
			LoaderPage,
			loadingPage,
			loadingGrafico,
			loadingEstacao,
			formattedDate,
			armazenadoPorcentagem,
			formatarNumero,
			dashboardGraficoEstacao,
            estacao,
            theme
		};
	},
};
</script>
<style scoped>
h6 {
  margin: 0;
}
</style>